import React, {
    useState,
    useEffect,
    useContext,
    useCallback,
    useMemo,
} from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { IconButton, Divider, Box } from '@mui/material'
import { listProjects, deleteProject } from '../api'
import {
    getOffset,
    Pagination,
    SearchFilter,
    DialogPopup,
    AddIcon,
    MoreVertIcon,
    ProjectsIcon,
    usePagination,
    useSearchParams,
    formatDate,
    defaultBackButton,
} from '../components'
import { AppContext } from '../context'
import { FeedbackContext } from '../feedback'
import { Griddable } from '../grid'
import { translate } from '../i18n'
import { Project, ProjectListParams, Projects } from '../model'
import ProjectPreviewPopup from './ProjectPreviewPopup'
import ProjectDeployPopup from './ProjectDeployPopup'
import ProjectMenu from './ProjectMenu'
import { RouterParams } from '../router'

type Popup = 'delete' | 'preview' | 'deploy' | 'qr'

export default function ProjectsList({ match }: RouterParams) {
    const history = useHistory()
    const context = useContext(AppContext)
    const feedbackContext = useContext(FeedbackContext)
    const paramTenantId = match.params.tenantId
    const tenantId = context.session?.tenant?.id ?? paramTenantId

    const isGrantedCreated = useMemo(
        () => context.isGrantedAny(['ProjectsCreate', 'TProjectsCreate']),
        [context]
    )

    const query = new URLSearchParams(useLocation().search)
    const params = useSearchParams<ProjectListParams>(query)

    const [status, setStatus] = useState<'loading' | 'loaded' | Error>(
        'loading'
    )
    const [data, setData] = useState<Projects>()

    const [project, setProject] = useState<Project>()
    const [popup, setPopup] = useState<Popup>()

    const clearCallback = useCallback(() => setData(undefined), [])
    const { page, pageSize, onChangedPage, onChangedPageSize } =
        usePagination(clearCallback)

    const retrievePromise = useCallback(() => {
        const offset = getOffset(page, pageSize)
        return listProjects({ ...params, pageSize, offset }, tenantId)
    }, [tenantId, page, pageSize, params])

    const reload = useCallback(() => {
        setStatus('loading')
        retrievePromise()
            .then((response) => {
                setData(response)
                setStatus('loaded')
            })
            .catch((error) => {
                setStatus(error)
            })
    }, [retrievePromise])

    useEffect(reload, [reload])

    const onClosePopup = useCallback(() => {
        setProject(undefined)
        setPopup(undefined)
    }, [])

    const onPopup = useCallback((project: Project, popup: Popup) => {
        setProject(project)
        setPopup(popup)
    }, [])

    const onDelete = useCallback(
        (project: Project) => onPopup(project, 'delete'),
        [onPopup]
    )

    const onPreview = useCallback(
        (project: Project) => onPopup(project, 'preview'),
        [onPopup]
    )

    const onQr = useCallback(
        (project: Project) => onPopup(project, 'qr'),
        [onPopup]
    )

    const onDeploy = useCallback(
        (project: Project) => onPopup(project, 'deploy'),
        [onPopup]
    )

    const onConfirmDelete = useCallback(() => {
        onClosePopup()
        if (!project) return

        feedbackContext.showBackdrop()
        deleteProject(project.id)
            .then(() => {
                feedbackContext.showSuccess(
                    translate('projects.delete.success', {
                        id: project.id,
                    }) as string
                )
                reload()
            })
            .catch((error) => feedbackContext.showError(error.message))
            .finally(feedbackContext.closeBackdrop)
    }, [onClosePopup, reload, project, feedbackContext])

    const onClicked = useCallback(
        (project: Project) => {
            history.push(
                `${paramTenantId ? `/tenants/${paramTenantId}` : ''}/projects/${
                    project.id
                }/edit`
            )
        },
        [history, paramTenantId]
    )

    return (
        <Pagination
            title={translate('projects.title')}
            subtitle={data?.tenant?.name}
            icon={<ProjectsIcon />}
            page={page}
            pageSize={pageSize}
            count={data ? data.items.length : 0}
            total={data ? data.total : 0}
            onChangedPage={onChangedPage}
            onChangedPageSize={onChangedPageSize}
            backButton={paramTenantId ? defaultBackButton : undefined}
            buttons={[
                {
                    text: translate('buttons.add'),
                    icon: <AddIcon />,
                    path: `${
                        paramTenantId ? `/tenants/${paramTenantId}` : ''
                    }/projects/new`,
                    available: isGrantedCreated,
                    variant: 'primary',
                },
            ]}
        >
            <SearchFilter
                label={translate('projects.filter') as string}
                query={query}
            />
            <Box mx={-2} mt={2}>
                <Divider />
                <Griddable
                    items={data ? data.items : []}
                    loading={status === 'loading'}
                    error={status instanceof Error ? status.message : undefined}
                    empty={translate('projects.empty') as string}
                    getId={(project: Project) => project.id}
                    clickable={{
                        onClick: onClicked,
                    }}
                    refreshable={{
                        columnId: 'actions',
                        onClick: reload,
                    }}
                    columns={[
                        {
                            title: translate('projects.id') as string,
                            converter: (project: Project) => project.id,
                            id: 'id',
                            fixed: 'left',
                        },
                        {
                            title: translate('projects.name') as string,
                            converter: (project: Project) => project.name,
                            id: 'name',
                        },
                        {
                            title: translate('projects.description') as string,
                            converter: (project: Project) =>
                                project.description || '---',
                            id: 'description',
                        },
                        {
                            title: translate('projects.created_at') as string,
                            converter: (project: Project) =>
                                formatDate({ date: project.created_at }),
                            id: 'created_at',
                        },
                        {
                            title: translate('projects.views') as string,
                            converter: (project: Project) => project.views ?? 0,
                            id: 'views',
                            textAlign: 'right',
                        },
                        {
                            title: (
                                <IconButton
                                    size="small"
                                    style={{ visibility: 'hidden' }}
                                    disabled
                                >
                                    <MoreVertIcon />
                                </IconButton>
                            ),
                            converter: (project: Project) => (
                                <ProjectMenu
                                    paramTenantId={paramTenantId}
                                    project={project}
                                    onPreview={onPreview}
                                    onQr={onQr}
                                    onDeploy={onDeploy}
                                    onDelete={onDelete}
                                />
                            ),
                            id: 'actions',
                            fixed: 'right',
                            width: 60,
                            textAlign: 'center',
                        },
                    ]}
                />
            </Box>
            {project && popup === 'delete' && (
                <DialogPopup
                    open
                    variant="error"
                    title={translate('projects.delete.title')}
                    onClose={onClosePopup}
                    button={{
                        text: translate('buttons.delete'),
                        onClick: onConfirmDelete,
                    }}
                >
                    {translate('projects.delete.text', { id: project.id })}
                </DialogPopup>
            )}
            {project && popup === 'preview' && (
                <ProjectPreviewPopup
                    stage="dev"
                    project={project}
                    onClose={onClosePopup}
                />
            )}
            {project && popup === 'qr' && (
                <ProjectPreviewPopup
                    stage="prod"
                    project={project}
                    onClose={onClosePopup}
                />
            )}
            {project && popup === 'deploy' && (
                <ProjectDeployPopup
                    project={project}
                    onClose={onClosePopup}
                    onDeployed={setProject}
                />
            )}
        </Pagination>
    )
}
